import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { getExistingSessionEmail } from "../cognito/cognitoAuth";
import { confirmPasswordReset } from "../cognito/cognitoPasswordAuth";
import { mapCognitoError } from "../cognito/mapCognitoError";
import { ResetPasswordView } from "./ResetPasswordView";

const PASSWORD_MIN_LENGTH = 12;

type ResetStep = "confirm" | "success";

export function ResetPassword(): ReactElement {
  const history = useHistory();
  const location = useLocation();
  const searchParameters = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const codeFromUrl = searchParameters.get("code");
  const emailFromUrl = searchParameters.get("email");

  const [step, setStep] = useState<ResetStep>("confirm");
  // Store URL params in state so they survive the replaceState call that strips the URL
  // eslint-disable-next-line react/hook-use-state
  const [email] = useState(() => emailFromUrl ?? "");
  // eslint-disable-next-line react/hook-use-state
  const [code] = useState(() => codeFromUrl ?? "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    void getExistingSessionEmail().then((sessionEmail) => {
      setHasActiveSession(sessionEmail !== undefined);
    });
  }, []);

  useEffect(() => {
    if (codeFromUrl) {
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    }
  }, [codeFromUrl]);

  useEffect(() => {
    // Guard against direct navigation without code/email params.
    // Check the state values (not URL-derived values) because the first useEffect
    // strips the URL params via replaceState — on re-renders the URL-derived values
    // would be null even though the state values are still valid.
    if (!email || !code) {
      history.replace("/login");
    }
  }, [email, code, history]);

  const handleConfirmReset = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    setError(undefined);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }

    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword)) {
      setError("Password must include at least one uppercase letter and one lowercase letter.");
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmPasswordReset(email, code, newPassword);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_CONFIRMED);
      setStep("success");
    } catch (authError: unknown) {
      const mappedError = mapCognitoError(authError, "password");
      logError(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_CONFIRM_FAILURE, {
        error: authError,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message, email },
      });

      setError(mappedError.message);
      showErrorToast(mappedError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResetPasswordView
      step={step}
      email={email}
      newPassword={newPassword}
      confirmPassword={confirmPassword}
      isSubmitting={isSubmitting}
      error={error}
      hasActiveSession={hasActiveSession}
      onNewPasswordChange={setNewPassword}
      onConfirmPasswordChange={setConfirmPassword}
      onSubmit={(formEvent) => {
        void handleConfirmReset(formEvent);
      }}
      onGoToLogin={() => {
        history.push("/login?mode=password");
      }}
    />
  );
}
